import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
  AttachFile as AttachIcon,
  Comment as CommentIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../apiBase';
import { useNavigate } from 'react-router-dom';

export default function TaskManagement() {
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    assigned_to: '',
    status: 'Task Ready'
  });

  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';

  useEffect(() => {
    fetchTasks();
    if (isSuperAdmin) {
      fetchUsers();
    }
  }, [user]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleOpenDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description,
        priority: task.priority,
        assigned_to: task.assigned_to || '',
        status: task.status
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        priority: 'Medium',
        assigned_to: '',
        status: 'Task Ready'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTask(null);
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const method = editingTask ? 'PUT' : 'POST';
      const url = editingTask 
        ? `${API_BASE_URL}/api/tasks/${editingTask.id}`
        : `${API_BASE_URL}/api/tasks`;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert('Failed to save task');
        return;
      }
      const savedTaskId = editingTask ? editingTask.id : data?.task?.id || data?.id;
      if (savedTaskId && selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i += 1) {
          const file = selectedFiles[i];
          const fd = new FormData();
          fd.append('file', file);
          await fetch(`${API_BASE_URL}/api/tasks/${savedTaskId}/attachments`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd
          }).catch(() => {});
        }
      }
      handleCloseDialog();
      setSelectedFiles([]);
      fetchTasks();
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const columns = [
    { title: 'Task Ready', status: 'Task Ready', color: '#ff9800' }, // Orange
    { title: 'In Progress', status: 'In Progress', color: '#2196f3' }, // Blue
    { title: 'Needs Review', status: 'Needs Review', color: '#9c27b0' }, // Purple
    { title: 'Done', status: 'Done', color: '#4caf50' } // Green
  ];

  const [selectedFiles, setSelectedFiles] = useState([]);
  const onFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const onDragStart = (e, task) => {
    try {
      e.dataTransfer.setData('text/plain', String(task.id));
      e.dataTransfer.effectAllowed = 'move';
    } catch {}
    setDraggingId(String(task.id));
  };

  const onDragOver = (e) => {
    e.preventDefault();
    try {
      e.dataTransfer.dropEffect = 'move';
    } catch {}
  };

  const moveTask = async (taskId, nextStatus) => {
    const token = localStorage.getItem('token');
    const prevTasks = tasks.slice();
    const nextTasks = prevTasks.map(t => String(t.id) === String(taskId) ? { ...t, status: nextStatus } : t);
    setTasks(nextTasks);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: nextStatus })
      });
      if (!res.ok) {
        setTasks(prevTasks);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (data && data.task) {
        setTasks(prev => prev.map(t => String(t.id) === String(taskId) ? { ...t, ...data.task } : t));
      } else {
        fetchTasks();
      }
    } catch {
      setTasks(prevTasks);
    }
  };

  const onDrop = async (e, columnStatus) => {
    e.preventDefault();
    const taskId = draggingId || (e.dataTransfer && e.dataTransfer.getData && e.dataTransfer.getData('text/plain')) || null;
    if (!taskId) return;
    await moveTask(taskId, columnStatus);
    setDraggingId(null);
  };

  const [draggingId, setDraggingId] = useState(null);

  return (
    <Layout title="Task Management">
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 72px)' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
            {/* Search tasks... input could go here */}
            <TextField 
                placeholder="Search tasks..." 
                size="small" 
                sx={{ width: 300, bgcolor: 'white', borderRadius: 1 }}
            />
            
            {isSuperAdmin && (
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ bgcolor: '#1a73e8' }}
            >
                Add Task
            </Button>
            )}
        </Stack>

        <Box sx={{ flexGrow: 1, display: 'flex', mb: -3, mx: -3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, height: '100%', width: '100%' }}>
          {columns.map((col) => (
            <Box key={col.status} sx={{ display: 'flex' }}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#f5f7fa',
                  height: '100%',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  overflowY: 'auto'
                }}
                elevation={0}
                data-status={col.status}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, col.status)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: col.color }} />
                    <Typography variant="h6" fontWeight="bold">
                        {col.title}
                    </Typography>
                </Box>

                {tasks
                  .filter(t => t.status === col.status)
                  .map((task) => (
                    <Card key={task.id} sx={{ borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} draggable onDragStart={(e) => onDragStart(e, task)}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                {task.priority}
                            </Typography>
                            {/* Actions */}
                            <IconButton size="small" onClick={() => navigate(`/tasks/${task.id}`)}>
                                <ViewIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        
                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                            {task.title}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: '#e0e0e0', color: '#666' }}>
                                {(task.assignee_name || task.assignee_username || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="caption" color="text.secondary">
                                Assigned to: {task.assignee_name || task.assignee_username || 'Unassigned'}
                            </Typography>
                        </Stack>
                        
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                             Created: {new Date(task.created_at).toISOString().slice(0, 10)}
                        </Typography>

                        <Stack direction="row" spacing={2} alignItems="center" sx={{ color: 'text.secondary' }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <CommentIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption">{Number(task.comments_count || 0)}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <AttachIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption">{Number(task.attachments_count || 0)}</Typography>
                            </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
              </Paper>
            </Box>
          ))}
        </Box>
        </Box>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editingTask ? 'Edit Task' : 'Add New Task'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Task Title"
                fullWidth
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                disabled={!isSuperAdmin && editingTask} // Users can't change title? Maybe allow? Assuming superadmin controls structure.
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <TextField
                select
                label="Priority"
                fullWidth
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                disabled={!isSuperAdmin}
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </TextField>

              {isSuperAdmin && (
                <TextField
                  select
                  label="Assign To"
                  fullWidth
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                >
                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                    {users.map(u => (
                        <MenuItem key={u.id} value={u.id}>
                            {u.full_name} ({u.role})
                        </MenuItem>
                    ))}
                </TextField>
              )}
              
              <TextField
                select
                label="Status"
                fullWidth
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {columns.map(col => (
                    <MenuItem key={col.status} value={col.status}>
                        {col.status}
                    </MenuItem>
                ))}
              </TextField>
              <Box>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<AttachIcon />}
                  sx={{ mr: 2 }}
                >
                  Add Attachments
                  <input hidden type="file" multiple onChange={onFilesSelected} />
                </Button>
                <Stack spacing={0.5}>
                  {selectedFiles.map((f, idx) => (
                    <Typography key={`${f.name}-${idx}`} variant="caption" color="text.secondary">
                      {f.name} ({Math.round((f.size || 0) / 1024)} KB)
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>Save</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
